import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { View, StyleSheet, type ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { AdminDialogModal } from '@/components/common/admin/mobile/AdminDialogModal';
import { GlassFormInput } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useSurveyDraft } from '@/hooks/residents/corkBoard/useSurveyDraft';
import { SurveyComposer } from '@/components/residents/corkBoard/survey/SurveyComposer';
import { AdminTagChipPicker } from './AdminTagChipPicker';
import type { CorkBoardTag, CreateSurveyThreadRequest } from '@/types/residents/corkBoard';

type PostKind = 'announcement' | 'survey';

interface AdminPostFormModalProps {
  visible: boolean;
  kind: PostKind;
  availableTags: CorkBoardTag[];
  isSavingAnnouncement?: boolean;
  isSavingSurvey?: boolean;
  onSaveAnnouncement?: (data: { title: string; body: string; tagIds: number[] }) => void;
  onSaveSurvey?: (data: Omit<CreateSurveyThreadRequest, 'propertyId'>) => void;
  onClose: () => void;
}

export const AdminPostFormModal: React.FC<AdminPostFormModalProps> = memo(
  ({
    visible,
    kind,
    availableTags,
    isSavingAnnouncement = false,
    isSavingSurvey = false,
    onSaveAnnouncement,
    onSaveSurvey,
    onClose,
  }) => {
    const { redesign } = useAdminColors();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [errors, setErrors] = useState<{ title?: string; body?: string; tags?: string }>({});
    const [showSurveyErrors, setShowSurveyErrors] = useState(false);

    const draft = useSurveyDraft();
    // Body ScrollView, handed to the composer so a new question can be scrolled into view.
    const scrollRef = useRef<ScrollView>(null);
    const { reset: resetDraft, setEnabled: setDraftEnabled, toPayload: draftToPayload, isValid: draftIsValid } = draft;

    const categoryTags = useMemo(
      () =>
        availableTags
          .filter((t) => t.slug !== 'announcements' && t.slug !== 'survey')
          .map((t) => (t.slug === 'events' ? { ...t, icon: null } : t)),
      [availableTags],
    );

    const surveyTag = useMemo(
      () => availableTags.find((t) => t.slug === 'survey') ?? null,
      [availableTags],
    );

    // Reset all local + draft state whenever the modal becomes visible.
    useEffect(() => {
      if (visible) {
        setTitle('');
        setBody('');
        setSelectedTagIds([]);
        setErrors({});
        setShowSurveyErrors(false);
        resetDraft();
        setDraftEnabled(kind === 'survey');
      }
    }, [visible, kind, resetDraft, setDraftEnabled]);

    const handleToggleTag = useCallback((tagId: number) => {
      setSelectedTagIds((prev) => {
        if (prev.includes(tagId)) return prev.filter((id) => id !== tagId);
        return [tagId];
      });
      setErrors((prev) => (prev.tags ? { ...prev, tags: undefined } : prev));
    }, []);

    const validate = useCallback((): boolean => {
      const newErrors: { title?: string; body?: string; tags?: string } = {};
      if (!title.trim()) {
        newErrors.title = 'Title is required';
      } else if (title.trim().length > CORK_BOARD_INPUT_LIMITS.titleMax) {
        newErrors.title = `Title must be ${CORK_BOARD_INPUT_LIMITS.titleMax} characters or fewer`;
      }
      // Body is optional for surveys (the questions are the content).
      if (kind !== 'survey' && !body.trim()) {
        newErrors.body = 'Body is required';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }, [title, body, kind]);

    const isSaving = kind === 'survey' ? isSavingSurvey : isSavingAnnouncement;
    const canSaveSurvey = kind === 'survey' ? draftIsValid : true;

    const handleSave = useCallback(() => {
      if (isSaving) return;
      if (kind === 'survey') setShowSurveyErrors(true);
      if (!validate() || !canSaveSurvey) return;
      if (kind === 'announcement') {
        onSaveAnnouncement?.({
          title: title.trim(),
          body: body.trim(),
          tagIds: selectedTagIds,
        });
      } else {
        onSaveSurvey?.({
          title: title.trim(),
          body: body.trim(),
          tagIds: selectedTagIds,
          kind: 'survey',
          survey: draftToPayload(),
        });
      }
    }, [
      isSaving,
      validate,
      canSaveSurvey,
      kind,
      onSaveAnnouncement,
      onSaveSurvey,
      title,
      body,
      selectedTagIds,
      draftToPayload,
    ]);

    const modalTitle = kind === 'survey' ? 'New Survey' : 'New Announcement';
    const saveText = kind === 'survey' ? 'Publish Survey' : 'Post Announcement';
    const savingText = kind === 'survey' ? 'Publishing...' : 'Posting...';
    const bodyPlaceholder =
      kind === 'survey' ? 'Optional context shown above the questions...' : 'Write your announcement...';

    return (
      <AdminDialogModal
        visible={visible}
        title={modalTitle}
        onClose={onClose}
        onSave={handleSave}
        isSaving={isSaving}
        saveText={saveText}
        savingText={savingText}
        maxWidth={kind === 'survey' ? 680 : 540}
        scrollRef={scrollRef}
      >
        <GlassFormInput
          label="Title"
          required
          value={title}
          onChangeText={(text) => {
            setTitle(text);
            if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
          }}
          placeholder={kind === 'survey' ? 'Survey title' : 'Announcement title'}
          maxLength={CORK_BOARD_INPUT_LIMITS.titleMax}
          error={errors.title}
          autoFocus
        />

        <GlassFormInput
          label="Body"
          required={kind !== 'survey'}
          value={body}
          onChangeText={(text) => {
            setBody(text);
            if (errors.body) setErrors((prev) => ({ ...prev, body: undefined }));
          }}
          placeholder={bodyPlaceholder}
          multiline
          maxLength={CORK_BOARD_INPUT_LIMITS.bodyMax}
          error={errors.body}
        />

        <View style={styles.field}>
          {kind === 'announcement' && (
            <Text style={[styles.autoTagNote, { color: redesign.text.subtle }]}>
              Announcements tag will be added automatically
            </Text>
          )}
          {kind === 'survey' && (
            <Text style={[styles.autoTagNote, { color: redesign.text.subtle }]}>
              Survey tag will be added automatically
            </Text>
          )}
          <AdminTagChipPicker
            tags={categoryTags}
            selectedTagIds={selectedTagIds}
            onToggleTag={handleToggleTag}
            lockedTag={kind === 'survey' ? surveyTag : null}
            label="Category tag (optional)"
            error={errors.tags}
          />
        </View>

        {kind === 'survey' && (
          <View style={styles.surveySection}>
            <SurveyComposer draft={draft} showErrors={showSurveyErrors} scrollRef={scrollRef} />
          </View>
        )}
      </AdminDialogModal>
    );
  },
);

AdminPostFormModal.displayName = 'AdminPostFormModal';

const styles = StyleSheet.create({
  field: {
    gap: 6,
  },
  autoTagNote: {
    fontSize: 12,
    fontStyle: 'italic',
    marginBottom: 4,
  },
  surveySection: {
    marginTop: 12,
  },
});
